// cspell:disable

import { CountryCode, findCountryCode } from "@redotech/locale/countries";
import { europeanUnionCountryCodes } from "@redotech/redo-inventory-common/fulfillment-country-code";
import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum ContentsType {
  MERCHANDISE = "merchandise",
  RETURNED_GOODS = "returned_goods",
  DOCUMENTS = "documents",
  GIFT = "gift",
  SAMPLE = "sample",
}

// Whether the party receiving a shipment is a business or an individual
// consumer. Used for customs clearance on international shipments (e.g. UPS
// requires a B2B/B2C indicator on EU-bound shipments). Absence means unknown,
// in which case carriers fall back to address heuristics.
export enum ConsigneeType {
  Business = "business",
  Consumer = "consumer",
}

export interface ConsigneeClassificationAddress {
  consigneeType?: ConsigneeType | null;
  residential?: boolean | null;
  company?: string | null;
}

// Shared B2B/B2C classification for cross-border customs indicators (UPS
// ConsigneeTypeValue, DHL isBusiness, etc.) so every carrier resolves the
// recipient type the same way. Order of preference:
//   1. The explicit signal derived from Shopify's order purchasing entity.
//   2. A verified residential/commercial flag — residential is a consumer, an
//      explicitly commercial address is a business.
//   3. A company name on the address (our best remaining hint).
// Defaults to consumer when there is no signal. Returns true for business.
export function isBusinessConsignee(
  address: ConsigneeClassificationAddress,
): boolean {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (address.consigneeType) {
    case ConsigneeType.Business:
      return true;
    case ConsigneeType.Consumer:
      return false;
    default:
      break;
  }
  if (address.residential != null) {
    return !address.residential;
  }
  if (address.company?.trim()) {
    return true;
  }
  return false;
}

const EU_COUNTRY_CODES = new Set<string>(europeanUnionCountryCodes);

export interface EuProductIdentifierApplicabilityRequest {
  isReturn?: boolean | null;
  fromAddress: ConsigneeClassificationAddress & { country?: string | null };
  toAddress: ConsigneeClassificationAddress & { country?: string | null };
}

/** Extra-EU origin → EU consumer sale. Returns and B2B are out of scope. */
export function shouldApplyEuProductIdentifiers(
  req: EuProductIdentifierApplicabilityRequest,
): boolean {
  if (req.isReturn) {
    return false;
  }
  if (isBusinessConsignee(req.toAddress)) {
    return false;
  }
  const originCountryCode = findCountryCode(req.fromAddress.country ?? "");
  const destinationCountryCode = findCountryCode(req.toAddress.country ?? "");
  return (
    originCountryCode != null &&
    destinationCountryCode != null &&
    !EU_COUNTRY_CODES.has(originCountryCode) &&
    EU_COUNTRY_CODES.has(destinationCountryCode)
  );
}

export interface DefaultCustomsSettings {
  contentsType: ContentsType;
  nonDeliveryOption: NonDeliveryOption;
  uspsExemptionCode: USPSExemptionCode;
}

export enum NonDeliveryOption {
  RETURN = "return",
  TREAT_AS_ABANDONED = "abandon",
}

export enum CustomsDeclarationType {
  CREATE = "create",
  PREDEFINED_VALUES = "predefined_values",
  LEAVE_BLANK = "leave_blank",
}

export enum AESExemptionType {
  ITN_TAX_ID = "itn_tax_id",
  USPS = "usps",
}

export enum USPSExemptionCode {
  NOEEI_30_36 = "NOEEI 30.36",
  NOEEI_30_37_A = "NOEEI 30.37(a)",
  NOEEI_30_37_B = "NOEEI 30.37(b)",
  NOEEI_30_37_C = "NOEEI 30.37(c)",
  NOEEI_30_37_D = "NOEEI 30.37(d)",
  NOEEI_30_37_F = "NOEEI 30.37(f)",
  NOEEI_30_37_G = "NOEEI 30.37(g)",
  NOEEI_30_37_H = "NOEEI 30.37(h)",
  NOEEI_30_37_I = "NOEEI 30.37(i)",
  NOEEI_30_37_J = "NOEEI 30.37(j)",
  NOEEI_30_37_K = "NOEEI 30.37(k)",
  NOEEI_30_37_L = "NOEEI 30.37(l)",
  NOEEI_30_37_M = "NOEEI 30.37(m)",
  NOEEI_30_37_N = "NOEEI 30.37(n)",
  NOEEI_30_37_O = "NOEEI 30.37(o)",
  NOEEI_30_37_P = "NOEEI 30.37(p)",
  NOEEI_30_37_S = "NOEEI 30.37(s)",
  NOEEI_30_37_T = "NOEEI 30.37(t)",
  NOEEI_30_37_U = "NOEEI 30.37(u)",
  NOEEI_30_37_V = "NOEEI 30.37(v)",
  NOEEI_30_37_W = "NOEEI 30.37(w)",
  NOEEI_30_37_X = "NOEEI 30.37(x)",
  NOEEI_30_37_Y = "NOEEI 30.37(y)",
}

export enum TaxIdUsage {
  "ALL" = "all",
  "OVER_2500" = "over_2500",
}

export enum DefaultContentsDescriptionSource {
  MANUAL = "manual",
  AI_GENERATED = "ai_generated",
}

export enum TaxIdType {
  // Employer identification number
  EIN = "EIN",
  // Economic Operator Registration and Identification. EU/UK
  EORI = "EORI",
  // Import One Stop Shop. EU
  IOSS = "IOSS",
  // Permanent Account Number. India
  PAN = "PAN",
  // Social Security Number. US
  SSN = "SSN",
  // Tax identification number
  TIN = "TIN",
  // UK internal markets scheme
  UKIMS = "UKIMS",
  // Value Added Tax
  VAT = "VAT",
  // VAT on eCommerce
  VOEC = "VOEC",
  // Postposed VAT accounting
  PVA = "PVA",
  GST = "GST",
}

export enum TaxIdEntity {
  Sender = "SENDER",
  Receiver = "RECEIVER",
}

export const TaxIdEntityLabel: Record<TaxIdEntity, string> = {
  [TaxIdEntity.Sender]: "Sender",
  [TaxIdEntity.Receiver]: "Receiver",
};

export const OutboundShipmentTaxIdSchema = z.object({
  entity: createEnum(TaxIdEntity, { enumName: "TaxIdEntity" }).meta({
    privacy: privacy.enumOrStatus,
  }),
  tax_id: z.string().meta({ privacy: privacy.id.government }),
  tax_id_type: z.string().meta({ privacy: privacy.enumOrStatus }),
  issuing_country: z.string().meta({ privacy: privacy.geo.coarse }),
});
export type OutboundShipmentTaxId = z.infer<typeof OutboundShipmentTaxIdSchema>;

export const TaxIdentifierSchema = z.object({
  _id: zExt.objectId().meta({ privacy: privacy.id.internal }),
  team: zExt.objectId().meta({ privacy: privacy.id.internal }),
  createdAt: z.date().meta({ privacy: privacy.timestamp }),
  updatedAt: z.date().meta({ privacy: privacy.timestamp }),
  nickname: z.string().meta({ privacy: privacy.resourceLabel }),
  taxIdType: createEnum(TaxIdType, { enumName: "TaxIdType" }).meta({
    privacy: privacy.enumOrStatus,
  }),
  issuingAuthority: z
    .string()
    .meta({ privacy: privacy.geo.coarse }) as z.ZodType<CountryCode>,
  number: z.string().meta({ privacy: privacy.id.government }),
  entity: createEnum(TaxIdEntity, { enumName: "TaxIdEntity" })
    .meta({ privacy: privacy.enumOrStatus })
    .nullish(),
});
export type TaxIdentifier = z.infer<typeof TaxIdentifierSchema>;

export const CreationTaxIdentifierSchema = TaxIdentifierSchema.omit({
  _id: true,
  team: true,
  createdAt: true,
  updatedAt: true,
});
export type CreationTaxIdentifier = z.infer<typeof CreationTaxIdentifierSchema>;

export const RedactedTaxIdentifierSchema = z.object({
  _id: zExt.objectId().meta({ privacy: privacy.id.internal }),
  nickname: z.string().meta({ privacy: privacy.resourceLabel }),
  taxIdType: createEnum(TaxIdType, { enumName: "TaxIdType" }).meta({
    privacy: privacy.enumOrStatus,
  }),
  issuingAuthority: z
    .string()
    .meta({ privacy: privacy.geo.coarse }) as z.ZodType<CountryCode>,
  redactedNumber: z.string().meta({ privacy: privacy.id.government }),
});
export type RedactedTaxIdentifier = z.infer<typeof RedactedTaxIdentifierSchema>;

export function redactTaxIdNumber(number: string): string {
  if (number.length < 2) {
    return "********";
  }
  return number[0] + "********" + number[number.length - 1];
}
export function getTaxIdTypeLabel(type: TaxIdType): string {
  switch (type) {
    case TaxIdType.EIN:
      return "EIN";
    case TaxIdType.EORI:
      return "EORI";
    case TaxIdType.IOSS:
      return "IOSS";
    case TaxIdType.PAN:
      return "PAN";
    case TaxIdType.SSN:
      return "SSN";
    case TaxIdType.TIN:
      return "TIN";
    case TaxIdType.UKIMS:
      return "UKIMS";
    case TaxIdType.VAT:
      return "VAT";
    case TaxIdType.VOEC:
      return "VOEC";
    case TaxIdType.PVA:
      return "PVA";
    case TaxIdType.GST:
      return "GST";
    default:
      return assertNever(type);
  }
}

export function getContentsTypeLabel(type: ContentsType): string {
  switch (type) {
    case ContentsType.DOCUMENTS:
      return "Documents";
    case ContentsType.GIFT:
      return "Gift";
    case ContentsType.MERCHANDISE:
      return "Merchandise";
    case ContentsType.RETURNED_GOODS:
      return "Returned goods";
    case ContentsType.SAMPLE:
      return "Sample";
    default:
      return assertNever(type);
  }
}

export function getNonDeliveryOptionLabel(option: NonDeliveryOption): string {
  switch (option) {
    case NonDeliveryOption.RETURN:
      return "Return to sender";
    case NonDeliveryOption.TREAT_AS_ABANDONED:
      return "Treat as abandoned";
    default:
      return assertNever(option);
  }
}

export function getCustomsDeclarationTypeLabel(
  type: CustomsDeclarationType,
): string {
  switch (type) {
    case CustomsDeclarationType.CREATE:
      return "Create declarations from order items";
    case CustomsDeclarationType.PREDEFINED_VALUES:
      return "Use pre-defined values";
    case CustomsDeclarationType.LEAVE_BLANK:
      return "Leave blank (enter manually)";
    default:
      return assertNever(type);
  }
}

export function getAESExemptionTypeLabel(code: AESExemptionType): string {
  switch (code) {
    case AESExemptionType.ITN_TAX_ID:
      return "ITN/Tax ID";
    case AESExemptionType.USPS:
      return "USPS exemption code";
    default:
      return assertNever(code);
  }
}

export function getATaxIdUsageLabel(usage: TaxIdUsage): string {
  switch (usage) {
    case TaxIdUsage.ALL:
      return "All international shipments";
    case TaxIdUsage.OVER_2500:
      return "International shipments where the total value is over $2500";
    default:
      return assertNever(usage);
  }
}

export function formatHsTariffCode(hsTariffNumber: string): string | undefined {
  // Remove all non-digit characters
  const digits = hsTariffNumber.replace(/\D/g, "");

  // HS codes must be at least 6 digits (chapter + subheading)
  if (digits.length < 6) {
    return undefined;
  }

  // Format as 9999.99 or 9999.99.99 or 9999.99.99.99
  const parts = [
    digits.slice(0, 4), // Chapter/Heading
    digits.slice(4, 6), // Subheading
    digits.slice(6, 8), // Tariff item (optional)
    digits.slice(8, 10), // Statistical suffix (optional)
  ].filter((part) => part.length > 0);

  return parts.join(".");
}
